import { Modifier } from "@dnd-kit/core";
import { Badge, Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { formatDistanceToNow } from "date-fns";

import { getObjectType } from "@/utils/libraryObjectUtils";

import { ObjectType } from "../../../types";
import { useDrive } from "../DriveContext";
import { DriveGridItem } from "../DriveGridItem";
import { DriveItem } from "../types";
import { itemImage } from "../utils";
import { restrictToBoundingRect } from "./restrictToBoundingRect";

function isMouseEvent(event: Event): event is MouseEvent {
  return event.type.startsWith("mouse");
}

const getDragOverlayFileType = (
  item: DriveItem,
): ObjectType | "canvas" | undefined => {
  if (item.__typename === "File") {
    return getObjectType(item);
  } else if (item.__typename === "Asset") {
    return "canvas";
  } else if (item.__typename === "Directory") {
    return "directory";
  } else if (
    item.__typename === "Style" ||
    item.__typename === "ReferenceSet"
  ) {
    return "style";
  } else {
    return undefined;
  }
};

export const modifierStartFromActivator: Modifier = ({
  activatorEvent,
  draggingNodeRect,
  transform,
  windowRect,
}) => {
  if (
    activatorEvent &&
    draggingNodeRect &&
    isMouseEvent(activatorEvent) &&
    windowRect
  ) {
    const newTransform = {
      ...transform,
      x: transform.x + activatorEvent.pageX - draggingNodeRect.left - 4,
      y: transform.y + activatorEvent.pageY - draggingNodeRect.top - 4,
    };
    return restrictToBoundingRect(newTransform, draggingNodeRect, windowRect);
  }
  return transform;
};

const TableOverlayContainer = styled(Box)(() => ({
  position: "relative",
  cursor: "grabbing",
  width: "50vw",
  height: 48,
}));

const GridOverlayContainer = styled(Box)(() => ({
  position: "relative",
  cursor: "grabbing",
  width: 150,
}));

const TableBadge = styled(Badge)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(-2),
  right: theme.spacing(-2),
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  borderRadius: "50%",
  fontSize: "0.75rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
}));

const GridBadge = styled(Badge)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(-2),
  right: theme.spacing(-2),
  zIndex: 1,
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  borderRadius: "50%",
  fontSize: "0.75rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
}));

const TableContentContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "start",
  borderRadius: "8px",
  overflow: "hidden",
  backgroundColor: theme.palette.secondary[6],
  boxShadow: "inset 0 0 0 0.5px rgba(65, 66, 91, 0.50)",
  color: theme.palette.secondary[1],
}));

const TableText = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 600,
  color: theme.palette.primary.contrastText,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  paddingLeft: "8px",
  paddingRight: "12px",
}));

export const DriveDragOverlay = () => {
  const { selectedItems, viewType } = useDrive();

  if (selectedItems.length === 0) {
    return null;
  }
  const firstItem = selectedItems[0];

  switch (viewType) {
    case "table":
      return (
        <TableOverlayContainer>
          {selectedItems.length > 1 && (
            <TableBadge badgeContent={selectedItems.length} />
          )}
          <TableContentContainer>
            <Box
              sx={{
                height: 48,
                width: 48,
                aspectRatio: "1 / 1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  height: 40,
                  width: 40,
                  aspectRatio: "1 / 1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "4px",
                    fontSize: "21px",
                  }}
                >
                  {itemImage(firstItem)}
                </Box>
              </Box>
            </Box>
            <TableText>{firstItem.name || "Untitled"}</TableText>
          </TableContentContainer>
        </TableOverlayContainer>
      );

    case "grid":
      return (
        <GridOverlayContainer>
          {selectedItems.length > 1 && (
            <GridBadge badgeContent={selectedItems.length} />
          )}
          <DriveGridItem
            text={firstItem.name || "Untitled"}
            subText={formatDistanceToNow(firstItem.createdAt, {
              addSuffix: true,
            })}
            isSelected={false}
            isDragging={true}
            src={
              (firstItem.__typename === "File" && firstItem.url) || undefined
            }
            thumbnails={
              firstItem.__typename === "File" && firstItem.thumbnails
                ? {
                    small: firstItem.thumbnails.small ?? undefined,
                    medium: firstItem.thumbnails.medium ?? undefined,
                    large: firstItem.thumbnails.large ?? undefined,
                  }
                : undefined
            }
            fileType={getDragOverlayFileType(firstItem)}
            onContextMenu={() => {}}
            onFileClick={() => {}}
            onFileDoubleClick={() => {}}
          />
        </GridOverlayContainer>
      );

    default:
      return null;
  }
};
